/**
 * FlowTooltip - Hover tooltip for flow nodes and edges
 *
 * Renders as a portal to avoid SVG clipping issues.
 * Shows different content for nodes vs edges.
 * Positions relative to cursor, constrained to viewport.
 */

import { memo, useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { formatCurrency, formatPercent } from '@/utils/format';
import { cn } from '@/utils/cn';

/**
 * Tooltip content for a node
 *
 * WP-FLOW-1: Added flowTotal for account nodes to show period activity
 * separately from balance display.
 */
export interface NodeTooltipContent {
  type: 'node';
  nodeType: 'income' | 'expense' | 'account' | 'lumped_account' | 'lumped';
  title: string;
  value: number;
  percentage?: number;
  transactionCount?: number;
  balance?: number;
  accountCount?: number;
  /** Transaction flow total for the period (for account nodes) */
  flowTotal?: number;
}

/**
 * Tooltip content for an edge
 */
export interface EdgeTooltipContent {
  type: 'edge';
  source: string;
  target: string;
  value: number;
  transactionCount?: number;
}

export type TooltipContent = NodeTooltipContent | EdgeTooltipContent;

export interface FlowTooltipProps {
  content: TooltipContent | null;
  position: { x: number; y: number };
  className?: string;
}

/**
 * Offset from cursor position
 */
const OFFSET = {
  x: 12,
  y: 12,
};

/**
 * Margin from viewport edges
 */
const VIEWPORT_MARGIN = 10;

function FlowTooltipComponent({
  content,
  position,
  className,
}: FlowTooltipProps) {
  const [adjustedPosition, setAdjustedPosition] = useState(position);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Adjust position to stay within viewport
  useEffect(() => {
    if (!content || !tooltipRef.current) {
      setAdjustedPosition(position);
      return;
    }

    const tooltip = tooltipRef.current;
    const rect = tooltip.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let x = position.x + OFFSET.x;
    let y = position.y + OFFSET.y;

    // Constrain to right edge
    if (x + rect.width > viewportWidth - VIEWPORT_MARGIN) {
      x = position.x - rect.width - OFFSET.x;
    }

    // Constrain to bottom edge
    if (y + rect.height > viewportHeight - VIEWPORT_MARGIN) {
      y = position.y - rect.height - OFFSET.y;
    }

    // Constrain to left edge
    if (x < VIEWPORT_MARGIN) {
      x = VIEWPORT_MARGIN;
    }

    // Constrain to top edge
    if (y < VIEWPORT_MARGIN) {
      y = VIEWPORT_MARGIN;
    }

    setAdjustedPosition({ x, y });
  }, [content, position]);

  // Don't render if no content
  if (!content) {
    return null;
  }

  const tooltipElement = (
    <div
      ref={tooltipRef}
      className={cn(
        'fixed z-50 pointer-events-none',
        'bg-gray-900 text-white text-sm rounded-lg shadow-lg',
        'px-3 py-2 max-w-xs',
        'animate-in fade-in-0 zoom-in-95 duration-150',
        className
      )}
      style={{
        left: adjustedPosition.x,
        top: adjustedPosition.y,
      }}
      role="tooltip"
      aria-live="polite"
    >
      {content.type === 'node' ? (
        <NodeTooltipBody content={content} />
      ) : (
        <EdgeTooltipBody content={content} />
      )}
    </div>
  );

  // Render as portal to body
  return createPortal(tooltipElement, document.body);
}

/**
 * Tooltip body for node content
 *
 * WP-FLOW-1: Updated to display balance prominently for account nodes,
 * with period activity (flowTotal) as secondary info. Category nodes
 * continue to show "Total" label.
 */
function NodeTooltipBody({ content }: { content: NodeTooltipContent }) {
  const getNodeTypeLabel = (nodeType: NodeTooltipContent['nodeType']): string => {
    switch (nodeType) {
      case 'income':
        return 'Income';
      case 'expense':
        return 'Expense';
      case 'account':
        return 'Account';
      case 'lumped_account':
        return 'Grouped Account';
      case 'lumped':
        return 'Grouped Accounts';
      default:
        return '';
    }
  };

  // Check if this is an account-type node (individual or lumped)
  const isAccountNode =
    content.nodeType === 'account' || content.nodeType === 'lumped_account';

  return (
    <div className="space-y-1">
      {/* Title row */}
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'inline-block w-2 h-2 rounded-full',
            content.nodeType === 'income' && 'bg-green-500',
            content.nodeType === 'expense' && 'bg-red-500',
            content.nodeType === 'account' && 'bg-blue-500',
            content.nodeType === 'lumped_account' && 'bg-blue-500',
            content.nodeType === 'lumped' && 'bg-purple-500'
          )}
        />
        <span className="font-medium">{content.title}</span>
      </div>

      {/* Type label */}
      <div className="text-gray-400 text-xs">
        {getNodeTypeLabel(content.nodeType)}
      </div>

      {/* Primary value display - Balance for accounts, Total for categories */}
      {isAccountNode ? (
        <>
          {/* Primary: Balance */}
          <div className="flex justify-between items-center">
            <span className="text-gray-300 text-sm">Balance</span>
            <span className="text-white font-semibold">
              {formatCurrency(content.balance ?? content.value)}
            </span>
          </div>
          {/* Secondary: Period activity (flowTotal) */}
          {content.flowTotal !== undefined && content.flowTotal !== 0 && (
            <div className="text-gray-400 text-xs">
              Period activity: {formatCurrency(content.flowTotal)}
            </div>
          )}
        </>
      ) : (
        /* Category nodes show "Total" */
        <div className="flex justify-between items-center">
          <span className="text-gray-300 text-sm">Total</span>
          <span className="text-white font-semibold">
            {formatCurrency(content.value)}
          </span>
        </div>
      )}

      {/* Percentage (for income/expense) */}
      {content.percentage !== undefined && (
        <div className="text-gray-300 text-xs">
          {formatPercent(content.percentage / 100)} of total
        </div>
      )}

      {/* Transaction count */}
      {content.transactionCount !== undefined && (
        <div className="text-gray-400 text-xs">
          {content.transactionCount} transaction{content.transactionCount !== 1 ? 's' : ''}
        </div>
      )}

      {/* Account count (for lumped) */}
      {content.accountCount !== undefined && content.accountCount > 0 && (
        <div className="text-gray-400 text-xs">
          {content.accountCount} account{content.accountCount !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}

/**
 * Tooltip body for edge content
 */
function EdgeTooltipBody({ content }: { content: EdgeTooltipContent }) {
  return (
    <div className="space-y-1">
      {/* Flow direction */}
      <div className="flex items-center gap-2">
        <span className="text-gray-300">{content.source}</span>
        <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
        </svg>
        <span className="text-gray-300">{content.target}</span>
      </div>

      {/* Amount */}
      <div className="text-white font-semibold">
        {formatCurrency(content.value)}
      </div>

      {/* Transaction count */}
      {content.transactionCount !== undefined && (
        <div className="text-gray-400 text-xs">
          {content.transactionCount} transaction{content.transactionCount !== 1 ? 's' : ''}
        </div>
      )}

      {/* Click hint */}
      <div className="text-gray-500 text-xs mt-1">
        Click to view transactions
      </div>
    </div>
  );
}

export const FlowTooltip = memo(FlowTooltipComponent);
export default FlowTooltip;
